import { Check, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SetupStepCta {
  label: string;
  /** Fired on click — open a dialog, navigate, etc. */
  onClick: () => void;
  disabled?: boolean;
}

export interface SetupStep {
  title: string;
  /** Whether this step is complete — drives the tick, strikethrough and bar. */
  done: boolean;
  /** Action link(s) shown on the right while the step is incomplete. */
  ctas: SetupStepCta[];
}

interface SetupProgressProps {
  steps: SetupStep[];
  /** Rough time-to-complete copy shown next to the step count. */
  totalTimeLabel?: string;
  /** Extra classes for the root card — e.g. `h-full` / `flex-1` for layout. */
  className?: string;
}

/**
 * "SETUP PROGRESS" onboarding checklist card. Purely presentational — every
 * step's `done` state is derived by the parent from live app data (Meta
 * connected, contacts added, campaign/automation launched), so the ticks,
 * strikethrough, light-green rows and progress bar always reflect reality.
 * A completed step hides its action link and shows a green check instead.
 */
export function SetupProgress({ steps, totalTimeLabel = 'about 5 minutes total', className }: SetupProgressProps) {
  const total = steps.length;
  const done = steps.filter((s) => s.done).length;

  return (
    <div className={cn("rounded-2xl bg-white border border-[#E4E7EC] p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04)]", className)}>
      {/* Header: label + step count */}
      <div className="flex flex-wrap items-center gap-4 justify-between">
        <div className="min-w-0">
          <p className="text-[13px] font-semibold uppercase tracking-wide" style={{ color: '#0F7A3D' }}>
            Setup progress
          </p>
          <p className="text-[15px] text-[#101828] mt-0.5">
            <span className="font-semibold">
              {done} of {total}
            </span>{' '}
            steps done · <span className="text-[#6B7280]">{totalTimeLabel}</span>
          </p>
        </div>
      </div>

      {/* Steps */}
      <div className="mt-4 grid gap-2">
        {steps.map((step, i) => (
          <div
            key={step.title}
            className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border ${
              step.done ? 'bg-[#F4FBF6] border-[#D0F0DC]' : 'bg-[#F9FAFB] border-[#E4E7EC]'
            }`}
          >
            <span
              className={`w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-[12px] font-semibold ${
                step.done ? 'text-white' : 'bg-white border border-[#E4E7EC] text-[#6B7280]'
              }`}
              style={step.done ? { backgroundColor: 'var(--secondary-color)' } : undefined}
            >
              {step.done ? <Check className="w-3.5 h-3.5" strokeWidth={3} /> : i + 1}
            </span>

            <span
              className={`flex-1 min-w-0 text-[14px] ${
                step.done ? 'font-medium line-through' : 'text-[#344054] font-medium'
              }`}
              style={step.done ? { color: '#0F7A3D', textDecorationColor: 'rgba(26,177,105,0.4)' } : undefined}
            >
              {step.title}
            </span>

            {!step.done && step.ctas.length > 0 && (
              <div className="flex items-center gap-3 shrink-0">
                {step.ctas.map((cta, ci) => (
                  <button
                    key={cta.label}
                    type="button"
                    onClick={cta.onClick}
                    disabled={cta.disabled}
                    className={`text-[13px] font-semibold flex items-center gap-1 transition-colors ${
                      cta.disabled ? 'text-[#98A2B3] cursor-not-allowed' : 'hover:opacity-80'
                    }`}
                    style={cta.disabled ? undefined : { color: 'var(--secondary-color)' }}
                  >
                    {cta.label}
                    {!cta.disabled && ci === step.ctas.length - 1 && <ArrowRight className="w-3.5 h-3.5" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
